import { useEffect, useState, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Button, MenuItem, IconButton, Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import TenantWorkflowStepsDrawer from './TenantWorkflowStepsDrawer';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  DOC_PARSER,
  ALTERNATIVE_PARSER,
  isAlternativeParser,
  normalizeParserType,
  parserTypeLabel,
} from '../../../utils/parserTypes';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const TenantWorkflow = () => {
  const { canEdit } = useFunctionPermissions(FC.TENANT_WORKFLOW);
  const [tenantWorkflows, setTenantWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState({
    documentTypeId: '',
    workflowDefinitionSnowflakeId: '',
    parserType: '',
    parserKey: '',
    parserSegmentSize: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [stepsDrawerOpen, setStepsDrawerOpen] = useState(false);
  const [stepsWorkflowId, setStepsWorkflowId] = useState(null);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [workflowDefinitionOptions, setWorkflowDefinitionOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [stepsWorkflowDefinitionId, setStepsWorkflowDefinitionId] = useState(null);

  // Toast notifications
  const { toastState, showSuccess, showError, hideToast } = useToast();

  const getTenantId = async () => {
    return localStorage.getItem('tenant_id');
  };

  const fetchDocumentTypeOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const tenantId = await getTenantId();
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-types`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!resp.ok) {
        throw new Error(
          `Failed to fetch document types options: ${resp.status} ${resp.statusText}`
        );
      }

      const data = await resp.json();
      return Array.isArray(data)
        ? data.filter((item) => item.name !== "Unidentified")
        : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fetchWorkflowDefinitionOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch workflow definitions: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      const [docTypes, workflows] = await Promise.all([
        fetchDocumentTypeOptions(),
        fetchWorkflowDefinitionOptions()
      ]);
      setDocumentTypeOptions(docTypes);
      setWorkflowDefinitionOptions(workflows);
      setOptionsLoading(false);
    };
    fetchOptions();
  }, []);

  const fetchTenantWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const tenantId = await getTenantId();
      if (!tenantId) throw new Error('No tenant ID found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-workflows`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error(`Failed to fetch tenant workflows: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      const tenantWorkflowsWithIds = Array.isArray(data) ? data : [];
      setTenantWorkflows(tenantWorkflowsWithIds);
    } catch (err) {
      setError(err.message);
      setTenantWorkflows([]);
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (creating = true, row = null) => {
    if (!creating && !canEdit) return;
    setIsCreating(creating);
    if (creating) {
      setForm({
        documentTypeId: '',
        workflowDefinitionSnowflakeId: '',
        parserType: '',
        parserKey: '',
        parserSegmentSize: '',
      });
    } else {
      setForm({
        documentTypeId: row.documentTypeId || '',
        workflowDefinitionSnowflakeId: row.workflowDefinitionSnowflakeId || '',
        parserType: normalizeParserType(row.parserType),
        parserKey: row.parserKey || '',
        parserSegmentSize: row.parserSegmentSize ?? row.raw?.parserSegmentSize ?? '',
      });
    }
    setSelectedRow(row || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'parserType') {
      setForm({
        ...form,
        parserType: value,
        // DocParser does not use key/segment size — clear when switching away from Alternative
        ...(value !== ALTERNATIVE_PARSER ? { parserKey: '', parserSegmentSize: '' } : {}),
      });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isCreating && !canEdit) return;

    const parserType = normalizeParserType(form.parserType);
    if (isAlternativeParser(parserType) && !String(form.parserKey || '').trim()) {
      showError('Parser Key is required for Alternative Parser');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const tenantId = await getTenantId();
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake`;
      const alternative = isAlternativeParser(parserType);
      const parserKey = alternative ? String(form.parserKey || '').trim() : '';
      const parserSegmentSize = alternative && form.parserSegmentSize !== '' && form.parserSegmentSize != null
        ? parseInt(form.parserSegmentSize, 10)
        : null;

      if (isCreating) {
        // For new records
        const body = {
          tenantId,
          documentTypeId: form.documentTypeId,
          workflowDefinitionSnowflakeId: form.workflowDefinitionSnowflakeId,
          parserType,
          parserKey,
          ...(parserSegmentSize != null && !Number.isNaN(parserSegmentSize) ? { parserSegmentSize } : {}),
        };

        const resp = await fetch(baseUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!resp.ok) {
          const errorData = await resp.json().catch(() => ({}));
          throw new Error(
            `Failed to create tenant workflow: ${resp.status} ${resp.statusText} - ${JSON.stringify(errorData)}`
          );
        }
      } else {
        // For updates, use the ID directly from selectedRow
        const updateBody = {
          ...selectedRow,  // Include all existing fields
          ...form,         // Override with form values
          parserType,
          parserKey,
          parserSegmentSize,
        };

        const updateUrl = `${baseUrl}/${selectedRow.id}`;
        const updateResp = await fetch(updateUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateBody),
        });

        if (!updateResp.ok) {
          const errorData = await updateResp.json().catch(() => ({}));
          throw new Error(
            `Failed to update tenant workflow: ${updateResp.status} ${updateResp.statusText} - ${JSON.stringify(errorData)}`
          );
        }
      }

      handleCloseDialog();
      fetchTenantWorkflows();
      showSuccess('Tenant workflow saved successfully');
    } catch (err) {
      console.error('Error in form submission:', err);
      showError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditModeToggle = () => {
    setIsEditMode(!isEditMode);
  };

  useEffect(() => {
    fetchTenantWorkflows();
  }, []);

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>
          Loading tenant workflows...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load tenant workflows
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchTenantWorkflows}
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    {
      field: 'workflowDefinitionName',
      headerName: 'Workflow Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'documentTypeName',
      headerName: 'Document Type',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'parserType',
      headerName: 'Parser Type',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => parserTypeLabel(params.row.parserType),
    },
    {
      field: 'parserKey',
      headerName: 'Parser Key',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'parserSegmentSize',
      headerName: 'Segment Size',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (params.value != null && params.value !== '' ? params.value : '-'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={canEdit ? 'Edit' : 'No edit permission'}>
          <span>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(false, params.row);
              }}
              disabled={!canEdit}
              size="small"
              sx={harmonateGridActionIconSx}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexShrink: 0,
      }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Tenant Workflows
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(true)}
            sx={{
              color: '#000000',
              borderColor: '#666',
              '&:hover': {
                borderColor: '#333',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              },
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            Add Workflow
          </Button> */}
          <IconButton
            onClick={fetchTenantWorkflows}
            sx={harmonatePlainIconButtonSx}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper elevation={0} sx={{
        ...harmonateCardSx,
        flex: 1,
        minHeight: 0,
        mb: 3,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <DataGridPro
          columnResizeMode="onEnd"
          resizable
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          autoHeight={false}
          rows={tenantWorkflows.map((workflow, idx) => ({
            id: workflow.id || workflow.workflowDefinitionSnowflakeId || idx + 1,
            name: workflow.workflowDefinitionName || '',
            inProcessCount: workflow.inProcessCount || 0,
            completedCount: workflow.completedCount || 0,
            awaitingApprovalCount: workflow.awaitingApprovalCount || 0,
            failedCount: workflow.failedCount || 0,
            tenantId: workflow.tenantId,
            documentTypeId: workflow.documentTypeId,
            documentTypeName: workflow.documentTypeName,
            workflowDefinitionSnowflakeId: workflow.workflowDefinitionSnowflakeId,
            workflowDefinitionName: workflow.workflowDefinitionName,
            parserType: workflow.parserType,
            parserKey: workflow.parserKey,
            parserSegmentSize: workflow.parserSegmentSize,
            raw: workflow
          }))}
          columns={columns}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: isEditMode ? 'transparent' : ht.secondaryTab,
              cursor: isEditMode ? 'default' : 'pointer'
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' }
          }}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          rowHeight={40}
          onRowClick={(params) => {
            if (!isEditMode) {
              setStepsWorkflowDefinitionId(params.row.workflowDefinitionSnowflakeId);
              setStepsDrawerOpen(true);
            }
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No tenant workflows available
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchTenantWorkflows}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>

      <TenantWorkflowStepsDrawer
        open={stepsDrawerOpen}
        onClose={() => setStepsDrawerOpen(false)}
        workflowDefinitionId={stepsWorkflowDefinitionId}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          {isCreating ? 'Add Tenant Workflow' : `Edit Tenant Workflow: ${selectedRow?.name}`}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              select
              label="Document Type"
              name="documentTypeId"
              value={form.documentTypeId || ''}
              onChange={handleFormChange}
              fullWidth
              size="small"
              disabled={optionsLoading}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {documentTypeOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Workflow Definition"
              name="workflowDefinitionSnowflakeId"
              value={form.workflowDefinitionSnowflakeId || ''}
              onChange={handleFormChange}
              fullWidth
              size="small"
              disabled={optionsLoading}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {workflowDefinitionOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Parser Type"
              name="parserType"
              value={normalizeParserType(form.parserType)}
              onChange={handleFormChange}
              fullWidth
              size="small"
              disabled={optionsLoading}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value={DOC_PARSER}>DocParser</MenuItem>
              <MenuItem value={ALTERNATIVE_PARSER}>Alternative Parser</MenuItem>
            </TextField>

            {isAlternativeParser(form.parserType) && (
              <>
                <TextField
                  fullWidth
                  required
                  label="Parser Key"
                  name="parserKey"
                  value={form.parserKey || ''}
                  onChange={handleFormChange}
                  size="small"
                  helperText="Required for Alternative Parser"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />
                <TextField
                  fullWidth
                  label="Segment Size"
                  name="parserSegmentSize"
                  type="number"
                  value={form.parserSegmentSize ?? ''}
                  onChange={handleFormChange}
                  size="small"
                  helperText="Pages per segment (defaults to 1 if blank)"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                  InputProps={{
                    inputProps: { min: 1, step: 1 },
                  }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || optionsLoading}
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Saving...' : (isCreating ? 'Add' : 'Update')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Toast Notifications */}
      <Toast
        open={toastState.open}
        message={toastState.message}
        severity={toastState.severity}
        onClose={hideToast}
      />
    </Box>
  );
};

export default TenantWorkflow;